import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@shared/utils/cn';

type SliderProps = React.ComponentProps<typeof SliderPrimitive.Root> & {
  value?: number[];
  defaultValue?: number[];
};

export function Slider({ className, value, defaultValue, min = 0, max = 100, ...props }: SliderProps) {
  const values = React.useMemo(() => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(defaultValue)) return defaultValue;
    // default single-thumb centered range when uncontrolled
    return [min as number];
  }, [value, defaultValue, min]);

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      value={value}
      defaultValue={defaultValue}
      min={min}
      max={max}
      className={cn(
        'relative flex w-full touch-none select-none items-center data-[disabled]:opacity-50',
        'data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          'relative grow overflow-hidden rounded-full bg-muted',
          'data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full',
          'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5',
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn('absolute bg-primary', 'data-[orientation=horizontal]:h-full', 'data-[orientation=vertical]:w-full')}
        />
      </SliderPrimitive.Track>
      {values.map((_, i) => (
        <SliderPrimitive.Thumb
          key={i}
          data-slot="slider-thumb"
          className={cn(
            'block size-4 shrink-0 rounded-full border border-primary bg-background shadow-sm',
            'transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 ring-ring/50 focus-visible:outline-none',
            'disabled:pointer-events-none disabled:opacity-50',
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export default Slider;

